import {
  defaultProductIds,
  defaultCategoryIds,
  type SavedSearch,
  type SearchFilters,
} from "@little-finds/core";

export const pendingGuestFiltersKey = "little-finds:pending-guest-filters";
export const deviceWishlistName = "My device wishlist";
export const filtersKey = (owner?: string | null) =>
  owner
    ? `little-finds:filters:${encodeURIComponent(owner)}`
    : "little-finds:filters";
export const defaultFilters = (): SearchFilters => ({
  product_ids: [...defaultProductIds],
  category_ids: [...defaultCategoryIds],
  center: { latitude: 39.8283, longitude: -98.5795 },
  radius_miles: 3000,
  scope: "us",
  zoom: 3,
  layers: { older: false, mentions: false, stockists: false },
});

/** Stored preferences are device input; malformed preferences must not break sign-in. */
export function parseFilters(raw: string | null): SearchFilters | undefined {
  if (!raw) return undefined;
  try {
    const value = JSON.parse(raw);
    if (
      !value ||
      !Array.isArray(value.product_ids) ||
      !Array.isArray(value.category_ids) ||
      !value.product_ids.every((id: unknown) => typeof id === "string") ||
      !value.category_ids.every((id: unknown) => id === "jellycat_bag_charm") ||
      !Number.isFinite(value.center?.latitude) ||
      !Number.isFinite(value.center?.longitude) ||
      Math.abs(value.center.latitude) > 90 ||
      Math.abs(value.center.longitude) > 180 ||
      !Number.isFinite(value.radius_miles) ||
      value.radius_miles <= 0 ||
      value.radius_miles > 3000 ||
      (value.scope !== undefined &&
        value.scope !== "us" &&
        value.scope !== "radius")
    )
      return undefined;
    const variants =
      value.variants &&
      typeof value.variants === "object" &&
      !Array.isArray(value.variants)
        ? Object.fromEntries(
            Object.entries(value.variants)
              .filter(([, v]) => typeof v === "string" && v.trim())
              .map(([k, v]) => [k, (v as string).trim().slice(0, 100)]),
          )
        : undefined;
    return {
      product_ids: [...new Set<string>(value.product_ids)].slice(0, 100),
      category_ids: [...new Set<string>(value.category_ids)],
      center: {
        latitude: value.center.latitude,
        longitude: value.center.longitude,
      },
      radius_miles: value.radius_miles,
      ...(value.scope === undefined ? {} : { scope: value.scope }),
      zoom: Number.isFinite(value.zoom)
        ? Math.max(1, Math.min(22, value.zoom))
        : 3,
      variants,
      layers: {
        older: value.layers?.older === true,
        mentions: value.layers?.mentions === true,
        stockists: value.layers?.stockists === true,
      },
      offset: 0,
    };
  } catch {
    return undefined;
  }
}

/** A deliberate anonymous edit is merged once; merely opening the app is not an edit. */
export function loginFilterSelection(
  guest: SearchFilters | undefined,
  local: SearchFilters | undefined,
  saved: SavedSearch[],
) {
  const existing = saved.find((s) => s.name === deviceWishlistName);
  if (guest) {
    const variants = { ...existing?.filters.variants };
    // Explicitly choosing "any size" clears that product's old constraint.
    for (const id of guest.product_ids) {
      if (guest.variants?.[id]) variants[id] = guest.variants[id];
      else delete variants[id];
    }
    const all =
      guest.product_ids.length === 0 && guest.category_ids.length === 0;
    const filters: SearchFilters = {
      ...guest,
      offset: 0,
      variants: all ? {} : variants,
      product_ids: all
        ? []
        : [
            ...new Set([
              ...guest.product_ids,
              ...(existing?.filters.product_ids ?? []),
            ]),
          ],
      category_ids: all
        ? []
        : [
            ...new Set([
              ...guest.category_ids,
              ...(existing?.filters.category_ids ?? []),
            ]),
          ],
    };
    return {
      filters,
      save: {
        id: existing?.id,
        name: deviceWishlistName,
        filters,
        alerts_enabled: existing?.alerts_enabled ?? false,
      },
    };
  }
  return {
    filters:
      local ?? existing?.filters ?? saved[0]?.filters ?? defaultFilters(),
    save: undefined,
  };
}
